// State of the first-run introduction: which step is open, the answers per
// step (seeded from the account the first time a step is shown) and saving
// them. The shell and the step components read and bind to `flow`.

import { tick } from 'svelte';
import { cubicOut } from 'svelte/easing';
import { t } from '../i18n.svelte';
import { prefs } from '../prefs.svelte';
import { session } from '../stores/session.svelte';
import { account } from '../stores/account.svelte';
import { data } from '../stores/data.svelte';
import { navigate } from '../router.svelte';
import { toast } from '../toast.svelte';
import { errorMessage } from '../api';
import { formatMoney, isoDate } from '../format';
import { setClientUi } from '../clients/helpers';
import { CLIENT_COLORS } from '../theme/palette';
import { enableNotifications, pushStatus, type EnableResult, type PushStatus } from './push';
import type { WorkSettings } from '../types';

export type StepKey = 'welcome' | 'work' | 'week' | 'period' | 'round' | 'client' | 'notif' | 'done';
/** The steps in order; each step's illustration has the same name. */
export const STEPS: StepKey[] = ['welcome', 'work', 'week', 'period', 'round', 'client', 'notif', 'done'];

export type PeriodChoice = 'weekly' | 'monthly' | 'quarterly' | 'custom';
type SavedKey = 'rate' | 'week' | 'period' | 'round' | 'client' | 'notif';

export const PRESET_ACTIVITIES = ['development', 'design', 'meeting', 'consultancy', 'projectmanagement', 'research', 'testing', 'support'];
export const ROUND_INCREMENTS = [5, 6, 10, 15, 30, 60];

// Reduced motion from the OS; the app preference is checked in `still`.
let osReduced = $state(false);
if (typeof window !== 'undefined') {
  const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
  osReduced = mq.matches;
  mq.addEventListener('change', () => (osReduced = mq.matches));
}

function track(name: string) {
  try {
    (window as unknown as { timepeakTrack?: (event: string, params: Record<string, unknown>) => void }).timepeakTrack?.('onboarding_step', { step: name });
  } catch { /* analytics must never break the app */ }
}

function quarterStart(d = new Date()): string {
  return isoDate(new Date(d.getFullYear(), Math.floor(d.getMonth() / 3) * 3, 1));
}

class OnboardingFlow {
  open = $state(false);
  index = $state(0);
  /** 1 = forward, -1 = back (direction of the step animation). */
  dir = $state(1);
  busy = $state(false);
  /** Counts step changes; the shell moves focus into the new step. */
  moves = $state(0);
  /** One summary line per saved step, shown on the last step. */
  saved = $state<Partial<Record<SavedKey, string>>>({});
  private seeded = new Set<StepKey>();

  // 2 · how you work
  rate = $state<number | null>(null);
  rateError = $state<string | null>(null);
  quick = $state(false);

  // 3 · work week
  hours = $state(40);
  days = $state<number[]>([1, 2, 3, 4, 5]);
  weekStart = $state(1);
  daysError = $state(false);

  // 4 · billing period
  periodChoice = $state<PeriodChoice>('monthly');
  customUnit = $state<'weeks' | 'months'>('weeks');
  customEvery = $state(2);
  customFrom = $state(isoDate(new Date()));

  // 5 · rounding
  roundOn = $state(false);
  roundMode = $state<'nearest' | 'up' | 'down'>('nearest');
  roundInc = $state(15);

  // 6 · first client
  clientName = $state('');
  clientEmail = $state('');
  clientRate = $state<number | null>(null);
  clientColor = $state<string | null>(CLIENT_COLORS[0]);
  chosen = $state<string[]>([]);
  customAct = $state('');
  clientError = $state<string | null>(null);
  emailError = $state<string | null>(null);
  created = $state<{ name: string; count: number } | null>(null);

  // 7 · notifications
  notifLongTimer = $state(true);
  notifLongBreak = $state(true);
  notifDaily = $state(false);
  push = $state<PushStatus>('default');
  pushResult = $state<EnableResult | null>(null);
  pushBusy = $state(false);

  get step(): StepKey {
    return STEPS[this.index];
  }

  /** No motion: OS setting or the app's preference. */
  get still() {
    return osReduced || prefs.value.reduceMotion;
  }
  get stepIn() {
    return this.still ? { duration: 0 } : { x: 28 * this.dir, duration: 280, delay: 90, easing: cubicOut };
  }
  get stepOut() {
    return this.still ? { duration: 0 } : { duration: 110 };
  }

  // ---------- Open, close, move ----------

  start() {
    this.index = 0;
    this.dir = 1;
    this.seeded.clear();
    this.created = null;
    this.saved = {};
    this.open = true;
    this.seed('welcome');
    track('welcome');
  }

  finish = (reason: 'done' | 'later', to?: string) => {
    if (!this.open) return;
    this.open = false;
    track(reason === 'done' ? 'finished' : `later:${this.step}`);
    prefs.update({ dismissed: { ...prefs.value.dismissed, onboarding: true } });
    if (session.user) session.user = { ...session.user, onboarding_completed: true };
    account.completeOnboarding().catch(() => { /* the preference above already keeps it closed */ });
    if (to) navigate(to);
  };

  go = (next: number) => {
    if (next < 0 || next >= STEPS.length) return;
    this.dir = next > this.index ? 1 : -1;
    this.index = next;
    this.seed(STEPS[next]);
    track(STEPS[next]);
    this.moves += 1;
  };

  /** "Next": save the step, then move on (stays when the step is invalid). */
  next = async (event?: Event) => {
    event?.preventDefault();
    if (this.busy || this.step === 'done') return;
    // Money fields commit on blur; make sure the last keystrokes count.
    (document.activeElement as HTMLElement | null)?.blur?.();
    await tick();
    this.busy = true;
    try {
      if (await this.save(this.step)) this.go(this.index + 1);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      this.busy = false;
    }
  };

  // ---------- Step helpers ----------

  toggleDay(d: number) {
    this.days = this.days.includes(d) ? this.days.filter((x) => x !== d) : [...this.days, d].sort();
    this.daysError = false;
  }

  setHours(value: number) {
    this.hours = Math.min(168, Math.max(0, Math.round(Number(value) || 0)));
  }

  toggleActivity(name: string) {
    this.chosen = this.chosen.includes(name) ? this.chosen.filter((n) => n !== name) : [...this.chosen, name];
  }

  addCustom() {
    const name = this.customAct.trim().slice(0, 100);
    if (!name) return;
    if (!this.chosen.some((n) => n.toLowerCase() === name.toLowerCase())) this.chosen = [...this.chosen, name];
    this.customAct = '';
  }

  /** Work settings for the chosen billing period. */
  periodPatch(): Partial<WorkSettings> {
    switch (this.periodChoice) {
      case 'weekly':
        return { period_type: 'weekly', period_duration: 1, period_start_day: 1, period_start_date: null };
      case 'quarterly':
        return { period_type: 'quarterly', period_duration: 1, period_start_day: 1, period_start_date: quarterStart() };
      case 'custom': {
        const every = Math.min(52, Math.max(1, Math.round(Number(this.customEvery) || 1)));
        const from = this.customFrom || isoDate(new Date());
        return { period_type: this.customUnit === 'weeks' ? 'weekly' : 'monthly', period_duration: every, period_start_day: Number(from.slice(8, 10)) || 1, period_start_date: from };
      }
      default:
        return { period_type: 'monthly', period_duration: 1, period_start_day: 1, period_start_date: null };
    }
  }

  turnOnPush = async () => {
    this.pushBusy = true;
    this.pushResult = await enableNotifications();
    this.push = pushStatus();
    this.pushBusy = false;
  };

  /** Save the notification step and continue in Settings. */
  notifSettings = async () => {
    this.busy = true;
    try {
      await this.save('notif');
    } catch { /* settings page shows the real state */ }
    this.busy = false;
    this.finish('done', '/app/instellingen/meldingen');
  };

  // ---------- Seeding and saving per step ----------

  private seed(key: StepKey) {
    if (this.seeded.has(key)) return;
    this.seeded.add(key);
    const work = account.work;
    switch (key) {
      case 'work':
        this.rate = prefs.value.defaultRate ?? null;
        this.quick = !!work.quick_time_input;
        break;
      case 'week':
        this.hours = Number(work.target_hours_per_week) || 40;
        this.days = [...account.workDays];
        this.weekStart = Number(work.week_start_day) === 0 ? 0 : 1;
        break;
      case 'period': {
        const duration = Math.max(1, Number(work.period_duration) || 1);
        const type = work.period_type || 'monthly';
        if (duration === 1 && type === 'quarterly') this.periodChoice = 'quarterly';
        else if (duration === 1 && type === 'weekly' && !work.period_start_date) this.periodChoice = 'weekly';
        else if (duration === 1 && type === 'monthly' && !work.period_start_date && (Number(work.period_start_day) || 1) === 1) this.periodChoice = 'monthly';
        else {
          this.periodChoice = 'custom';
          this.customUnit = type === 'weekly' ? 'weeks' : 'months';
          this.customEvery = type === 'quarterly' ? duration * 3 : duration;
          this.customFrom = work.period_start_date || isoDate(new Date());
        }
        break;
      }
      case 'round': {
        const r = account.rounding;
        this.roundOn = !!r.rounding_enabled;
        this.roundMode = r.rounding_mode === 'up' || r.rounding_mode === 'down' ? r.rounding_mode : 'nearest';
        this.roundInc = ROUND_INCREMENTS.includes(Number(r.rounding_increment)) ? Number(r.rounding_increment) : 15;
        break;
      }
      case 'client':
        this.clientRate = this.rate ?? prefs.value.defaultRate ?? null;
        break;
      case 'notif': {
        const n = account.notifications;
        this.notifLongTimer = !!n.notifLongTimer;
        this.notifLongBreak = !!n.notifLongBreak;
        this.notifDaily = !!n.notifDailySummary;
        this.push = pushStatus();
        break;
      }
    }
  }

  private async save(key: StepKey): Promise<boolean> {
    switch (key) {
      case 'work': {
        if (this.rate === null || this.rate < 0) {
          this.rateError = t('onb.work.rate_required');
          document.getElementById('onb-rate')?.focus();
          return false;
        }
        this.rateError = null;
        prefs.update({ defaultRate: this.rate });
        if (this.quick !== !!account.work.quick_time_input) await account.saveWork({ quick_time_input: this.quick });
        this.saved.rate = t('onb.done.sum_rate', { rate: formatMoney(this.rate) });
        return true;
      }
      case 'week': {
        if (!this.days.length) {
          this.daysError = true;
          return false;
        }
        await account.saveWork({ target_hours_per_week: this.hours, work_days: [...this.days].sort().join(','), week_start_day: this.weekStart });
        this.saved.week = t('onb.done.sum_week', { hours: this.hours, days: this.days.length });
        return true;
      }
      case 'period':
        await account.saveWork(this.periodPatch());
        this.saved.period = t('onb.done.sum_period', { period: t(`onb.period_word.${this.periodChoice}`) });
        return true;
      case 'round':
        if (this.roundOn) {
          await account.saveRounding({ rounding_enabled: true, rounding_mode: this.roundMode, rounding_increment: this.roundInc });
          this.saved.round = t('onb.done.sum_round', { minutes: this.roundInc });
        } else if (account.rounding.rounding_enabled) {
          await account.saveRounding({ rounding_enabled: false });
          delete this.saved.round;
        }
        return true;
      case 'client':
        return this.saveClient();
      case 'notif': {
        const n = account.notifications;
        const patch = {
          notifLongTimer: this.notifLongTimer,
          notifLongBreak: this.notifLongBreak,
          notifDailySummary: this.notifDaily,
          ...(this.pushResult === 'enabled' ? { browserNotifications: true } : {})
        };
        const changed = Object.entries(patch).some(([k, v]) => n[k as keyof typeof n] !== v);
        if (changed) await account.saveNotifications(patch);
        if (this.pushResult === 'enabled' || this.push === 'granted') this.saved.notif = t('onb.done.sum_notif');
        return true;
      }
      default:
        return true;
    }
  }

  private async saveClient(): Promise<boolean> {
    if (this.created) return true;
    const name = this.clientName.trim();
    const email = this.clientEmail.trim();
    this.clientError = name ? null : t('onb.client.name_required');
    this.emailError = email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) ? t('onb.client.email_invalid') : null;
    if (this.clientError || this.emailError) {
      document.getElementById(this.clientError ? 'onb-client-name' : 'onb-client-email')?.focus();
      return false;
    }
    const client = await data.createClient({ name: name.slice(0, 100), email: email || null, default_rate: this.clientRate ?? null });
    if (this.clientColor) setClientUi(client.id, { color: this.clientColor });
    let count = 0;
    for (const activity of this.chosen) {
      try {
        await data.createActivity({ name: activity, client_id: client.id });
        count += 1;
      } catch (err) {
        toast.error(errorMessage(err));
      }
    }
    this.created = { name: client.name, count };
    this.saved.client = t('onb.done.sum_client', { name: client.name });
    return true;
  }
}

export const flow = new OnboardingFlow();
